// --- CONFIGURATION CONSTANTS ---
// Baseline stat value (average starter pokemon stat)
export const BASELINE_STAT = 50;

// Multiplier for converting Resistance to Max HP
export const HP_MULTIPLIER = 3;

// Maximum Cooldown Reduction (30%)
export const MAX_CDR = 0.3;

// Maximum Dodge Chance (35%)
export const MAX_DODGE = 0.35;

// Divisor for CDR calculation (higher = slower scaling)
export const CDR_DIVISOR = 1100;

// Divisor for Dodge calculation (higher = slower scaling)
export const DODGE_DIVISOR = 1500;

// Level scaling factor (3% per level)
export const LEVEL_SCALE_FACTOR = 0.03;

// Divisor for Healing scaling (AP - Baseline) / Divisor
export const HEALING_SCALE_DIVISOR = 100;

// --- FORMULA FUNCTIONS ---

/**
 * Calculate the level multiplier for stats
 * Formula: 1 + (level - 1) * 0.03
 */
export const getLevelMultiplier = (level: number): number => {
  return 1 + (level - 1) * LEVEL_SCALE_FACTOR;
};

/**
 * Calculate Max HP based on effective resistance
 * Formula: effectiveResistance * 2.5
 */
export const getMaxHP = (effectiveResistance: number): number => {
  return Math.round(effectiveResistance * HP_MULTIPLIER);
};

/**
 * Calculate damage multiplier based on effective Ability Power
 * Formula: 1 + (effectiveAP - 50) / 60
 * Reduced scaling to avoid excessive damage at high AP
 */
export const getDamageMultiplier = (effectiveAP: number): number => {
  const diff = effectiveAP - BASELINE_STAT;
  return 1 + diff / 60;
};

/**
 * Calculate healing multiplier based on effective Ability Power
 * Formula: 1 + (effectiveAP - 50) / 100
 */
export const getHealingMultiplier = (effectiveAP: number): number => {
  return 1 + (effectiveAP - BASELINE_STAT) / HEALING_SCALE_DIVISOR;
};

/**
 * Calculate cooldown multiplier based on effective Speed
 * Formula: min(0.30, max(0, (effectiveSpeed - 50) / 1100))
 * Returns a value between 0.70 (max reduction) and 1.0 (no reduction)
 */
export const getCooldownMultiplier = (effectiveSpeed: number): number => {
  const cdr = Math.max(0, (effectiveSpeed - BASELINE_STAT) / CDR_DIVISOR);
  const finalCdr = Math.min(MAX_CDR, cdr);
  return 1 - finalCdr;
};

/**
 * Calculate dodge chance based on effective Speed
 * Formula:
 *  - Up to 25%: Linear scaling (1% per ~15 speed)
 *  - 25% to 35%: Diminishing returns (1% per ~75 speed) - 5x harder
 * Returns a probability between 0.0 and 0.35
 */
export const getDodgeChance = (effectiveSpeed: number): number => {
  const dodge = (effectiveSpeed - BASELINE_STAT) / DODGE_DIVISOR;
  const SOFT_CAP = 0.25;

  // Up to 25%, linear scaling
  if (dodge <= SOFT_CAP) {
    return Math.max(0, dodge);
  }

  // After 25%, scaling is 5x harder (diminishing returns)
  const excess = dodge - SOFT_CAP;
  return Math.min(MAX_DODGE, SOFT_CAP + excess / 5);
};
